import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  generatePractice,
  getFavoriteMistakes,
  getMistakes,
  getRedoHistory,
  redoMistake,
  submitPractice,
  toggleMistakeFavorite,
} from '@/api/student';
import { AiLoading } from '@/components/ui/ai-loading/ai-loading';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import type { GeneratedPractice, MistakeFlatItem, PracticeResult, RedoResult } from '@/types';

import styles from './mistakes.module.css';

type TabKey = 'overview' | 'items' | 'favorites' | 'redos';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: '薄弱概览' },
  { key: 'items', label: '错题集' },
  { key: 'favorites', label: '我的收藏' },
  { key: 'redos', label: '重做记录' },
];

export function MistakesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>('overview');
  const [open, setOpen] = useState<number | null>(null);

  // 重做弹窗
  const [redoTarget, setRedoTarget] = useState<MistakeFlatItem | null>(null);
  const [redoContent, setRedoContent] = useState('');
  const [redoResult, setRedoResult] = useState<RedoResult | null>(null);
  const [redoSubmitting, setRedoSubmitting] = useState(false);
  const [redoError, setRedoError] = useState('');

  // 专项练习弹窗
  const [practice, setPractice] = useState<GeneratedPractice | null>(null);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [practiceKp, setPracticeKp] = useState('');
  const [practiceAnswers, setPracticeAnswers] = useState<string[]>([]);
  const [practiceImportText, setPracticeImportText] = useState('');
  const [practiceResult, setPracticeResult] = useState<PracticeResult | null>(null);
  const [practiceSubmitting, setPracticeSubmitting] = useState(false);
  const [practiceError, setPracticeError] = useState('');

  // 薄弱知识点详情弹窗
  const [weakOpen, setWeakOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['mistakes'],
    queryFn: () => getMistakes(),
  });

  const { data: favorites } = useQuery({
    queryKey: ['mistakes', 'favorites'],
    queryFn: () => getFavoriteMistakes(),
    enabled: tab === 'favorites',
  });

  const { data: redos } = useQuery({
    queryKey: ['mistakes', 'redos'],
    queryFn: () => getRedoHistory(),
    enabled: tab === 'redos',
  });

  const favoriteMutation = useMutation({
    mutationFn: ({ submissionId, evidenceId }: { submissionId: number; evidenceId: string }) =>
      toggleMistakeFavorite(submissionId, evidenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mistakes'] });
    },
  });

  const items: MistakeFlatItem[] = data?.items ?? [];
  const maxDeduction =
    data?.mistakes.reduce((m, x) => Math.max(m, x.total_deduction), 0) || 1;

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['mistakes'] });
  };

  const startRedo = (item: MistakeFlatItem) => {
    setRedoTarget(item);
    setRedoContent(item.content ?? '');
    setRedoResult(null);
    setRedoError('');
  };

  const submitRedo = async () => {
    if (!redoTarget) return;
    if (!redoContent.trim()) {
      setRedoError('请写下你的新答案');
      return;
    }
    setRedoSubmitting(true);
    setRedoError('');
    try {
      const result = await redoMistake(
        redoTarget.submission_id,
        redoTarget.evidence.id,
        redoContent.trim(),
      );
      setRedoResult(result);
      refreshAll();
    } catch (err) {
      setRedoError(err instanceof Error ? err.message : '提交失败，请重试');
    } finally {
      setRedoSubmitting(false);
    }
  };

  const runPractice = async (kp: string) => {
    setPractice(null);
    setPracticeResult(null);
    setPracticeError('');
    setPracticeImportText('');
    setPracticeKp(kp);
    setPracticeLoading(true);
    try {
      const res = await generatePractice(kp);
      setPractice(res);
      setPracticeAnswers(new Array(res.questions.length).fill(''));
    } catch (err) {
      setPracticeError(err instanceof Error ? err.message : '生成练习失败，请稍后重试');
    } finally {
      setPracticeLoading(false);
    }
  };

  const updatePracticeAnswer = (index: number, value: string) => {
    setPracticeAnswers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const applyImportedAnswers = () => {
    if (!practice) return;
    const lines = practiceImportText
      .split(/\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const parsed: string[] = [];
    lines.forEach((line) => {
      const m = line.match(/^\d+[\.、]\s*(.*)$/);
      parsed.push(m ? m[1] : line);
    });
    setPracticeAnswers((prev) => {
      const next = [...prev];
      for (let i = 0; i < Math.min(parsed.length, next.length); i += 1) {
        next[i] = parsed[i];
      }
      return next;
    });
    setPracticeImportText('');
  };

  const submitPracticeAnswers = async () => {
    if (!practice) return;
    if (!practiceAnswers.some((a) => a.trim())) {
      setPracticeError('请至少完成一道题');
      return;
    }
    setPracticeSubmitting(true);
    setPracticeError('');
    try {
      const result = await submitPractice(practice.id, practiceAnswers);
      setPracticeResult(result);
    } catch (err) {
      setPracticeError(err instanceof Error ? err.message : '提交失败，请重试');
    } finally {
      setPracticeSubmitting(false);
    }
  };

  const exportPdf = () => {
    const exportItems = items.length > 0 ? items : (favorites ?? []);
    if (exportItems.length === 0) return;
    const html = `
<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="UTF-8" />
<title>错题试卷 - AI 智能批阅</title>
<style>
  body { font-family: "Microsoft YaHei", sans-serif; padding: 32px; color: #1f2937; }
  h1 { font-size: 20px; text-align: center; }
  .meta { text-align: center; color: #6b7280; font-size: 12px; margin-bottom: 24px; }
  .item { border: 1px solid #d1d5db; border-radius: 8px; padding: 14px 16px; margin-bottom: 14px; page-break-inside: avoid; }
  .item h3 { margin: 0 0 6px; font-size: 15px; }
  .tag { color: #dc2626; font-size: 12px; }
  .content { white-space: pre-wrap; font-size: 13px; line-height: 1.8; background: #f9fafb; padding: 10px; border-radius: 6px; margin: 8px 0; }
  .fix { font-size: 13px; color: #16a34a; }
  .answer-space { height: 90px; border-bottom: 1px dashed #9ca3af; margin-top: 8px; }
</style>
</head>
<body>
  <h1>我的错题试卷</h1>
  <div class="meta">共 ${exportItems.length} 道错题 · 生成时间 ${new Date().toLocaleString()} · 请在下方空白处重做</div>
  ${exportItems
    .map(
      (it, i) => `
  <div class="item">
    <h3>第 ${i + 1} 题（${it.job_title} · ${it.evidence.dimension} <span class="tag">扣 ${it.evidence.deduction} 分</span>）</h3>
    <div class="content">${(it.content ?? '（图片作业，无文字内容）').replace(/</g, '&lt;')}</div>
    <div class="fix">错误原因：${it.evidence.description}${it.evidence.location ? `（位置：${it.evidence.location}）` : ''}</div>
    <div class="answer-space"></div>
  </div>`,
    )
    .join('')}
</body>
</html>`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  const renderMistakeCard = (item: MistakeFlatItem) => (
    <Card key={item.key} className={styles.mistakeCard}>
      <div className={styles.mistakeHead}>
        <div className={styles.mistakeHeadInfo}>
          <span className={styles.mistakeJob}>{item.job_title}</span>
          <span className={styles.mistakeDate}>{new Date(item.date).toLocaleDateString()}</span>
        </div>
        <button
          type="button"
          className={`${styles.starBtn} ${item.favorite ? styles.starred : ''}`}
          title={item.favorite ? '取消收藏' : '收藏这道错题'}
          onClick={() =>
            favoriteMutation.mutate({
              submissionId: item.submission_id,
              evidenceId: item.evidence.id,
            })
          }
        >
          {item.favorite ? '★' : '☆'}
        </button>
      </div>

      <div className={styles.mistakeEvidence}>
        <div className={styles.mistakeEvidenceHead}>
          <span className={styles.mistakeDim}>{item.evidence.dimension}</span>
          <span className={styles.mistakeDeduction}>扣 {item.evidence.deduction} 分</span>
        </div>
        <p className={styles.mistakeDesc}>{item.evidence.description}</p>
        {item.evidence.location && (
          <p className={styles.mistakeLoc}>位置：{item.evidence.location}</p>
        )}
      </div>

      {item.content && (
        <details className={styles.mistakeContent}>
          <summary>我的原始作答</summary>
          <pre className={styles.mistakeContentText}>{item.content}</pre>
        </details>
      )}

      <div className={styles.mistakeActions}>
        <Button size="small" onClick={() => startRedo(item)}>
          重做此题
        </Button>
        <Button
          size="small"
          variant="secondary"
          onClick={() => navigate(`/review/${item.submission_id}`)}
        >
          查看批阅详情
        </Button>
      </div>
    </Card>
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>错题本</h1>
          <p className={styles.subtitle}>
            自动归集每道错题，支持重做再批阅、收藏与导出打印。
          </p>
        </div>
        <Button variant="secondary" onClick={exportPdf} disabled={items.length === 0}>
          导出错题试卷
        </Button>
      </header>

      <div className={styles.tabs}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={tab === t.key ? styles.tabActive : styles.tab}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'items' && items.length > 0 && (
              <span className={styles.tabBadge}>{items.length}</span>
            )}
            {t.key === 'favorites' && (favorites?.length ?? 0) > 0 && (
              <span className={styles.tabBadge}>{favorites!.length}</span>
            )}
          </button>
        ))}
      </div>

      {isLoading && <div className={styles.empty}>加载中...</div>}

      {/* ---------- 薄弱概览 ---------- */}
      {!isLoading && tab === 'overview' && (
        <>
          <div className={styles.summary}>
            <Card
              className={`${styles.summaryCard} ${styles.summaryCardClickable}`}
              onClick={() => setWeakOpen(true)}
              role="button"
              tabIndex={0}
              aria-label="查看薄弱知识点详情"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setWeakOpen(true);
                }
              }}
            >
              <div className={styles.summaryValue}>{data?.total ?? 0}</div>
              <div className={styles.summaryLabel}>薄弱知识点</div>
            </Card>
            <Card
              className={`${styles.summaryCard} ${styles.summaryCardClickable}`}
              onClick={() => setTab('items')}
              role="button"
              tabIndex={0}
              aria-label="查看全部错题"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setTab('items');
                }
              }}
            >
              <div className={styles.summaryValue}>{items.length}</div>
              <div className={styles.summaryLabel}>累计错题</div>
            </Card>
            <Card
              className={`${styles.summaryCard} ${styles.summaryCardClickable}`}
              onClick={() => navigate('/report')}
              role="button"
              tabIndex={0}
              aria-label="查看学情报告"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/report');
                }
              }}
            >
              <div className={styles.summaryValue}>{data?.reviewed_count ?? 0}</div>
              <div className={styles.summaryLabel}>已批阅作业</div>
            </Card>
          </div>

          {(data?.mistakes.length ?? 0) === 0 && (
            <div className={styles.empty}>暂无错题，继续加油！</div>
          )}

          <div className={styles.list}>
            {data?.mistakes.map((m, idx) => (
              <Card key={m.knowledge_point} className={styles.item}>
                <button
                  type="button"
                  className={styles.itemHead}
                  onClick={() => setOpen(open === idx ? null : idx)}
                >
                  <div className={styles.kpInfo}>
                    <span className={styles.kpName}>{m.knowledge_point}</span>
                    <span className={styles.kpCount}>出现 {m.count} 次</span>
                  </div>
                  <div className={styles.barWrap}>
                    <div
                      className={styles.bar}
                      style={{
                        width: `${Math.max(8, (m.total_deduction / maxDeduction) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className={styles.kpDeduction}>共扣 {m.total_deduction} 分</span>
                </button>

                {open === idx && (
                  <div className={styles.examples}>
                    {m.examples.map((ex, i) => (
                      <div
                        key={i}
                        className={styles.example}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/review/${ex.submission_id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') navigate(`/review/${ex.submission_id}`);
                        }}
                      >
                        <div className={styles.exampleJob}>{ex.job_title}</div>
                        <div className={styles.exampleDetail}>{ex.detail}</div>
                        <div className={styles.exampleSuggestion}>
                          位置：{ex.suggestion} · 扣 {ex.deduction} 分
                        </div>
                        <div className={styles.exampleLink}>查看批阅详情 →</div>
                      </div>
                    ))}
                    <Button
                      size="small"
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        runPractice(m.knowledge_point);
                      }}
                    >
                      AI 生成专项练习
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </>
      )}

      {/* ---------- 错题集 / 收藏 ---------- */}
      {!isLoading && tab === 'items' && (
        <div className={styles.list}>
          {items.length === 0 && <div className={styles.empty}>暂无错题，继续加油！</div>}
          {items.map(renderMistakeCard)}
        </div>
      )}

      {!isLoading && tab === 'favorites' && (
        <div className={styles.list}>
          {(favorites ?? []).length === 0 && (
            <div className={styles.empty}>
              还没有收藏错题，点击错题卡片右上角的 ☆ 收藏重要错题。
            </div>
          )}
          {(favorites ?? []).map(renderMistakeCard)}
        </div>
      )}

      {/* ---------- 重做记录 ---------- */}
      {!isLoading && tab === 'redos' && (
        <div className={styles.list}>
          {(redos ?? []).length === 0 && (
            <div className={styles.empty}>暂无重做记录，去错题集里挑战一道吧！</div>
          )}
          {(redos ?? []).map((r, i) => (
            <Card key={i} className={styles.mistakeCard}>
              <div className={styles.redoHead}>
                <span className={styles.redoScore}>
                  重做得分 {r.score}/{r.max_score}
                </span>
                <span className={styles.mistakeDate}>
                  {new Date(r.date).toLocaleString()}
                </span>
              </div>
              <pre className={styles.mistakeContentText}>{r.content}</pre>
              {(r.feedback ?? []).length > 0 && (
                <ul className={styles.redoFeedback}>
                  {r.feedback.map((f, fi) => (
                    <li key={fi}>
                      {f.dimension}：{f.description}（扣 {f.deduction} 分）
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* ---------- 重做弹窗 ---------- */}
      {redoTarget && (
        <div className={styles.dialogMask} role="dialog" aria-modal="true">
          <div className={styles.dialog}>
            <h2 className={styles.dialogTitle}>重做错题</h2>
            <p className={styles.dialogSub}>
              {redoTarget.job_title} · {redoTarget.evidence.dimension}（原扣{' '}
              {redoTarget.evidence.deduction} 分）
            </p>
            <p className={styles.dialogErrorHint}>{redoTarget.evidence.description}</p>

            <AiLoading
              overlay
              active={redoSubmitting}
              title="AI 正在批阅你的新答案"
              hint={['正在重新审题…', '正在逐项分析得分点…', '正在撰写批阅反馈…']}
            />

            {!redoResult ? (
              <>
                <textarea
                  className={styles.dialogTextarea}
                  value={redoContent}
                  onChange={(e) => setRedoContent(e.target.value)}
                  rows={8}
                  placeholder="在这里写下你的新答案，提交后将由 AI 重新批阅"
                />
                {redoError && <div className={styles.dialogError}>{redoError}</div>}
                <div className={styles.dialogActions}>
                  <Button
                    variant="secondary"
                    onClick={() => setRedoTarget(null)}
                    disabled={redoSubmitting}
                  >
                    取消
                  </Button>
                  <Button loading={redoSubmitting} onClick={submitRedo}>
                    提交并 AI 批阅
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className={styles.redoResultScore}>
                  新得分：{redoResult.score} / {redoResult.max_score}
                </div>
                {redoResult.comment && (
                  <p className={styles.redoResultComment}>{redoResult.comment}</p>
                )}
                {redoResult.evidences.length > 0 && (
                  <ul className={styles.redoFeedback}>
                    {redoResult.evidences.map((f, fi) => (
                      <li key={fi}>
                        {f.dimension}：{f.description}（扣 {f.deduction} 分）
                      </li>
                    ))}
                  </ul>
                )}
                <div className={styles.dialogActions}>
                  <Button onClick={() => setRedoTarget(null)}>完成</Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ---------- 专项练习弹窗 ---------- */}
      {(practiceLoading || practice) && (
        <div className={styles.dialogMask} role="dialog" aria-modal="true">
          <div className={styles.dialog}>
            <h2 className={styles.dialogTitle}>
              专项练习：{practice ? practice.knowledge_point : practiceKp}
            </h2>
            {practiceLoading ? (
              <AiLoading title="AI 正在生成练习题" />
            ) : practiceResult ? (
              <>
                <div className={styles.practiceResultScore}>
                  得分：{practiceResult.score} / {practiceResult.max_score}
                </div>
                <div className={styles.practiceFeedbackList}>
                  {practiceResult.feedback.map((f, i) => (
                    <div key={i} className={styles.practiceFeedbackItem}>
                      <div className={styles.practiceFeedbackHead}>
                        <span className={styles.practiceFeedbackTitle}>第 {i + 1} 题</span>
                        <span className={styles.practiceFeedbackScore}>
                          {f.score} / {f.max_score}
                        </span>
                      </div>
                      <p className={styles.practiceFeedbackQuestion}>{f.question}</p>
                      <p className={styles.practiceFeedbackAnswer}>你的答案：{f.user_answer || '（未作答）'}</p>
                      <p className={styles.practiceFeedbackComment}>{f.comment}</p>
                    </div>
                  ))}
                </div>
                <div className={styles.dialogActions}>
                  <Button onClick={() => setPractice(null)}>关闭</Button>
                </div>
              </>
            ) : (
              <>
                <AiLoading
                  overlay
                  active={practiceSubmitting}
                  title="AI 正在批改练习"
                  hint={['正在核对每道题的答案…', '正在分析解题步骤…', '正在生成针对性点评…']}
                />
                <p className={styles.dialogSub}>请逐题作答，也可以从下方批量导入答案后统一提交。</p>

                <div className={styles.importAnswers}>
                  <textarea
                    className={styles.dialogTextarea}
                    value={practiceImportText}
                    onChange={(e) => setPracticeImportText(e.target.value)}
                    rows={2}
                    placeholder="粘贴答案，例如：&#10;1. 答案一&#10;2. 答案二"
                  />
                  <Button size="small" variant="secondary" onClick={applyImportedAnswers}>
                    导入答案
                  </Button>
                </div>

                <div className={styles.practiceQuestionList}>
                  {practice!.questions.map((q, i) => (
                    <div key={i} className={styles.practiceQuestionItem}>
                      <div className={styles.practiceQuestionHead}>
                        <span className={styles.practiceQuestionIndex}>第 {i + 1} 题</span>
                      </div>
                      <p className={styles.practiceQuestionText}>{q.question}</p>
                      <textarea
                        className={styles.dialogTextarea}
                        value={practiceAnswers[i] || ''}
                        onChange={(e) => updatePracticeAnswer(i, e.target.value)}
                        rows={4}
                        placeholder="在这里写下你的答案"
                      />
                    </div>
                  ))}
                </div>

                {practiceError && <div className={styles.dialogError}>{practiceError}</div>}

                <div className={styles.dialogActions}>
                  <Button variant="secondary" onClick={() => setPractice(null)}>
                    取消
                  </Button>
                  <Button loading={practiceSubmitting} onClick={submitPracticeAnswers}>
                    提交并 AI 批改
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ---------- 薄弱知识点详情弹窗 ---------- */}
      {weakOpen && (
        <div className={styles.dialogMask} role="dialog" aria-modal="true">
          <div className={styles.dialog}>
            <h2 className={styles.dialogTitle}>薄弱知识点详情</h2>
            <p className={styles.dialogSub}>按累计扣分排序，定位最需要巩固的维度</p>
            {(data?.mistakes.length ?? 0) === 0 && (
              <div className={styles.empty}>暂无薄弱知识点，继续保持！</div>
            )}
            <div className={styles.weakList}>
              {data?.mistakes.map((m) => (
                <div key={m.knowledge_point} className={styles.weakItem}>
                  <div className={styles.weakItemHead}>
                    <span className={styles.weakName}>{m.knowledge_point}</span>
                    <span className={styles.weakMeta}>
                      出现 {m.count} 次 · 共扣 {m.total_deduction} 分
                    </span>
                  </div>
                  <div className={styles.barWrap}>
                    <div
                      className={styles.bar}
                      style={{
                        width: `${Math.max(5, (m.total_deduction / maxDeduction) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.dialogActions}>
              <Button onClick={() => setWeakOpen(false)}>关闭</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
